import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject} from "@angular/core";
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  RecaptchaModule,
  RecaptchaSettings,
  RecaptchaV3Module,
  ReCaptchaV3Service,
  RECAPTCHA_SETTINGS,
  RECAPTCHA_V3_SITE_KEY,
} from "ng-recaptcha";
import { cloneDeep } from "lodash-es";
import { Location } from "@angular/common";
import { FlexLayoutModule } from "@ngbracket/ngx-layout";
import { MatButtonModule } from "@angular/material/button";
import { Subscription } from "rxjs";

import {
  AccountabilityMethod,
  Company,
  RegistrationMode,
} from "~/core/models/company";
import { AuthenticationService } from "~/core/services/authentication.service";
import { CompanyService } from "~/core/services/company.service";
import { NavigationService } from "~/core/services/navigation.service";
import { SnackbarService } from "~/core/services/snackbar.service";
import { ApiErrorParser } from "~/core/helpers/api-error-parser";
import { BetSafeService } from "~/core/services/betsafe.service";
import { StInputComponent } from "~/dash/core/st-input/st-input.component";
import { AlertComponent } from "~/dash/core/alert/alert.component";
import { UtilsService } from "~/core/helpers/utils";
import { StorageService } from "~/core/services/storage.service";
import { CookiePermissionService } from "~/core/services/cookie-permission.service";
import { UserInformationConfirmationDialogComponent } from "~/containers/dialogs/user-information-confirmation-dialog/user-information-confirmation-dialog.component";
import {
  UserStatusEnum,
  UserStatusReasonEnum,
} from "~/core/enum/userStatusEnum";
import { UserProfile } from "~/core/constants/user-profile";
import { KycValidationService } from "~/core/helpers/kyc-validation.service";
import { User } from "~/core/models/user";
import { KycTypeEnum } from "~/core/enum/kyc-type.enum";
import { ActionType } from "~/pages/back-office/config/client-config/add-edit-kyc-action/add-edit-kyc-action.component";
import { RequestErrorCode } from "~/core/enum/request-error-code.enum";
import { StModalConfirmationComponent } from "~/dash/core/st-modal-confirmation/st-modal-confirmation.component";
import { NeedLocationDialogComponent } from "~/containers/dialogs/need-location-dialog/need-location-dialog.component";
import { ThemeService } from "~/core/services/theme.service";
import { SessionTimeService } from "~/core/services/session-time.service";
import { ReactivationDialogComponent } from "../dialogs/reactivation-dialog/reactivation-dialog.component";
import { HelperPlus2Service } from "~/core/services/helper-plus2.service";
import { CurrencyCasePipeModule } from "~/core/helpers/currency-case-pipe/currency-case.module";
import { SECRET_KEY_V2, SECRET_KEY_V3 } from "~/core/constants/constants";
import { RecaptchaFallback } from "~/core/entity/recaptcha-fallback";
import { provideNgxMask } from "ngx-mask";
import {StTextAreaComponent} from "~/dash/core/st-text-area/st-text-area.component";
import {StOtpInputComponent} from "~/dash/core/st-otp-input/st-otp-input.component";
import { DestroyService } from '~/core/services/destroy.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: "app-global-login",
  templateUrl: "./global-login.component.html",
  styleUrls: ["./global-login.component.scss"],
  imports: [
    CurrencyCasePipeModule,
    FlexLayoutModule,
    TranslateModule,
    StInputComponent,
    RecaptchaModule,
    RecaptchaV3Module,
    MatButtonModule,
    AlertComponent,
    StOtpInputComponent,
    ReactiveFormsModule
  ],
  providers: [DestroyService, 
    provideNgxMask(),
    ReCaptchaV3Service,
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: SECRET_KEY_V3 },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: SECRET_KEY_V2 } as RecaptchaSettings,
    }
  ],
})
export class GlobalLoginComponent
  extends RecaptchaFallback
  implements OnInit, OnDestroy
{
  private readonly destroy$ = inject(DestroyService);

  @ViewChild("recaptchaV2") recaptchaV2: any;
  @ViewChild("usernameInput") usernameInput: any;

  @Input() title: string;
  @Input() image: string;
  @Input() min: string;
  @Input() max: string;
  @Input() gameName: string;
  @Input() subtitle: string;
  @Input() invertedLabel: boolean;
  @Input() customButtonOptions: { title: string; action: (...params) => void };
  @Output() backTo: EventEmitter<any> = new EventEmitter<any>();
  @Output() useBonusCallback: EventEmitter<any> = new EventEmitter<any>();
  companyLogo: string;

  loginForm: UntypedFormGroup;

  locationEnabled = null;
  permissionLocationStatus;

  companyCredit = true;
  company: Company;
  isRegister;
  idAffiliate;
  showAuth = false;
  alertMessage = null;
  showRecaptcha = false;
  isLoggedIn = false;
  isGuestPlayer = false;
  isEnableRegistration = false;
  isEnableGuestPlayers = false;

  public loadingExit = false;
  public lastUsernameLoggedKey = "LAST_USERNAME";

  protected subscriptions: Array<Subscription> = [];
  protected readonly RequestErrorCode = RequestErrorCode;

  error;
  @Input() loading: boolean;
  @Output() loadingChange: EventEmitter<any> = new EventEmitter();
  @Output() toggleLoginSideMenuEvent = new EventEmitter();
  @Output() successLogin = new EventEmitter();
  @Output() errorLogin = new EventEmitter();
  @Input() minWidth: string;
  @Input() hideOptions: boolean;
  @Input() displayGame = false;
  @Input() hideRegisterButton: boolean;

  showAuthErrorCodes = [
    RequestErrorCode.INVALID_2FA_CODE,
    RequestErrorCode.MISSING_RECAPTCHA_MFA,
    RequestErrorCode.INVALID_INTERVAL_BETWEEN_CODE_GENERATION
  ];
  protected readonly ThemeService = ThemeService;
  instaceModalReactivation: any;
  containerWidth = 75;
  containerHeight = 100;
  imageLoaded = true;

  constructor(
    public dialog: MatDialog,
    public authentication: AuthenticationService,
    public fb: UntypedFormBuilder,
    public themeService: ThemeService,
    public translate: TranslateService,
    public router: Router,
    public companyService: CompanyService,
    public route: ActivatedRoute,
    public recaptchaV3Service: ReCaptchaV3Service,
    public snackbarService: SnackbarService,
    public betSafeService: BetSafeService,
    protected navigation: NavigationService,
    protected cookiesService: CookiePermissionService,
    protected storageService: StorageService,
    public location: Location,
    protected kycValidationService: KycValidationService,
    public readonly apiErrorParser: ApiErrorParser,
    public sessionTimeService: SessionTimeService,
    protected helper: HelperPlus2Service,
  ) {
    super();
  }

  ngOnInit() {
    this.getCompanyLogo();
    const params = cloneDeep(this.route.snapshot.queryParams);
    if (params?.id) {
      this.idAffiliate = params.id;
    }
    this.initForm();
    this.updatePropertiesByCompany();
    this.subscriptions.push(
      this.companyService.currentCompanyObservable().pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updatePropertiesByCompany();
      }),
    );
    this.loginListener();
  }

  protected loginListener() {
    this.subscriptions.push(
      this.authentication.loginEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.isLoggedIn = true;
        this.isGuestPlayer = this.authentication.isGuestPlayer();
      }),
    );

    this.subscriptions.push(
      this.authentication.logoutEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.isLoggedIn = false;
        this.isGuestPlayer = false;
      }),
    );

    this.subscriptions.push(
      this.authentication.isLoggedInObservable.pipe(takeUntil(this.destroy$)).subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        this.isGuestPlayer = this.authentication.isGuestPlayer();
      }),
    );
  }

  updatePropertiesByCompany() {
    this.company = CompanyService.currentCompany;
    if (window.location.href.includes("register")) {
      this.handleRegisterRoute();
    }
    this.isLoggedIn = this.authentication.isLoggedIn();
    this.isEnableRegistration = this.enableRegistration();
    this.isEnableGuestPlayers = UtilsService.isEnabledGuestPlayers(
      this.companyService,
    );
    this.companyCredit =
      this.company.accountability_method !== AccountabilityMethod.COUNTING;
    if (
      AuthenticationService.isCurrentUserClient() ||
      this.hasOnlineAgentActive()
    ) {
      this.companyCredit = true;
    }
  }

  async hasOnlineAgentActive() {
    const userType = AuthenticationService.currentUser?.type;
    const companyAgent = CompanyService.currentCompany.online_agent;
    const userAgentEnabled =
      AuthenticationService.currentUser?.zone?.online_agent?.enable;
    return (
      [
        UserProfile.MASTER,
        UserProfile.ADMIN,
        UserProfile.USERS_ADMIN,
        UserProfile.EVENTS_ADMIN
      ].includes(userType) ||
      ([UserProfile.MANAGER, UserProfile.SUPERVISOR].indexOf(userType) !== -1 &&
        companyAgent &&
        userAgentEnabled)
    );
  }

  enableRegistration() {
    return this.company && this.company.client_registration;
  }

  handleRegisterRoute() {
    const params = new URLSearchParams(window.location.search);
    const queryParams: Record<string, string> = Object.fromEntries(
      params.entries(),
    );

    if (queryParams) {
      this.idAffiliate = queryParams.id;

      this.authentication.handleRegisterRoute(queryParams);
    }
    if (!this.isRegister && this.dialog.openDialogs.length === 0) {
      this.isRegister = true;
      this.openRegisterDialog();
    }
  }

  openRegisterDialog(isClick?: boolean): void {
    if (!this.storageService.cookiesEssentialsAllowed) {
      this.subscriptions.push(
        this.cookiesService.permissions$.essentials.pipe(takeUntil(this.destroy$)).subscribe(
          (isGranted: boolean) => {
            if (isGranted) {
              this.afterHandleRegister(isClick);
            }
          },
        ),
      );
    } else {
      this.afterHandleRegister(isClick);
    }
  }

  private afterHandleRegister(isClick: boolean) {
    if (this.company.registration_mode === RegistrationMode.GLOBAL) {
      this.router.navigate(["register-user"]);
    } else {
      this.authentication.openRegisterScreen(this.location, isClick);
    }
  }

  getCompanyLogo() {
    this.company = CompanyService.currentCompany;
    this.companyLogo = this.company?.logo_light?.length
      ? this.company.logo_light
      : this.company?.logo;
  }

  get isDesktop() {
    return UtilsService.isDesktop();
  }

  ngOnDestroy() {
    clearInterval(this.permissionLocationStatus);
  }

  backToPage() {
    this.backTo.emit();
  }

  initForm(): void {
    const lastUsername = localStorage.getItem(
      this.authentication.lastUsernameLoggedKey,
    );
    this.loginForm = this.fb.group({
      username: this.fb.control(lastUsername, [Validators.required]),
      password: this.fb.control(null, [Validators.required]),
      auth_code: this.fb.control(null, []),
      useBonus: this.fb.control(null, []),
    });
  }

  onImageError() {
    this.imageLoaded = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.image) {
      this.imageLoaded = true;
    }
  }

  loginSubmit() {
    if (this.loginForm.valid) {
      const data = this.loginForm.getRawValue();
      this.error = null;
      this.loading = true;
      this.loadingChange.emit(true);

      this.validateRequestWithRecaptcha(data);
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  validUserMessage(): string {
    // const companyConfig = this.companyService.getCurrentCompanyConfig();
    // const options =
    //   companyConfig.customization?.authentication?.login_options || [];
    // const enabledOptions = options.filter((option) => option.enable);
    // if (enabledOptions?.length === 0) {
    //   return this.translate.instant("ONLINE_CLIENT.USERNAME");
    // }
    // const messages = enabledOptions.map((option) =>
    //   this.translate.instant(`ONLINE_CLIENT.${option.type}`),
    // );
    // return messages.join(` ${this.translate.instant("GENERAL.OR")} `);
    return this.translate.instant("ONLINE_CLIENT.USERNAME");
  }

  showReactivationDialog(error) {
    const panelClass = [];
    const isDarkMode =
      CompanyService.currentCompanyConfig?.customization?.registration
        ?.dark_mode;

    if (isDarkMode) {
      panelClass.push("THEME_DEFAULT", "register-dialog-ref");
    } else {
      panelClass.push("register-dialog-ref");
    }
    if (!UtilsService.isDesktop()) {
      panelClass.push("mobile-fullscreen");
    }
    this.instaceModalReactivation = this.dialog.open(
      ReactivationDialogComponent,
      {
        panelClass,
        disableClose: true,
        autoFocus: false,
        width: UtilsService.isDesktop() ? "500px" : "100%",
        maxWidth: UtilsService.isDesktop() ? "80vw" : "100%",
        height: UtilsService.isDesktop() ? "auto" : "100%",
        maxHeight: UtilsService.isDesktop() ? "90vh" : "initial",
      },
    );

    if (isDarkMode) {
      const overlayContainers = document.getElementsByClassName(
        "register-dialog-ref",
      );
      if (overlayContainers?.length > 0) {
        const overlayContainer = overlayContainers[0];
        overlayContainer.setAttribute("data-theme", "dark");
      }
    }
    this.instaceModalReactivation.componentInstance.isLoginView = true;
    if (error?.code == RequestErrorCode.SELF_EXCLUSION_ACTIVE) {
      this.instaceModalReactivation.componentInstance.isTemporarilyModal = false;
    } else if (error?.code == RequestErrorCode.SELF_EXCLUSION_TEMPORARILY) {
      this.instaceModalReactivation.componentInstance.days =
        error?.info?.expiration_in_days;
      this.instaceModalReactivation.componentInstance.date =
        UtilsService.calculateNextDate(error?.info?.expiration_in_days);
      this.instaceModalReactivation.componentInstance.isTemporarilyModal = true;
    }
  }

  login(data, token?: string, type?: string) {
    this.checkLocation();
    this.alertMessage = null;
    this.authentication.login(data, token, type).pipe(takeUntil(this.destroy$)).subscribe(
      (user) => {
        localStorage.setItem(this.lastUsernameLoggedKey, data.username);
        this.loading = false;
        this.loadingChange.emit(false);
        this.isLoggedIn = true;
        this.showRecaptcha = false;
        this.shouldUseRecaptchaV2 = false;
        this.sessionTimeService.startSession(user?.token_expiration_date);
        this.authentication.loginEvent.emit();
        this.successLogin.emit();
        this.sendMessageLogin();
      },
      (error) => {
        this.handleLoginError(error, data);
      },
    );
  }

  private handleLoginError(error: any, data: any) {
    this.errorLogin.emit();

    if (this.isSelfExclusionError(error)) {
      this.showReactivationDialog(error);
      this.loading = false;
      this.loadingChange.emit(false);
      return;
    }

    if (error?.code === RequestErrorCode.USER_NEEDS_TO_ENABLE_2FA) {
      this.handleUserNeedsTwoFactor(data);
      return;
    }

    if (error?.code === RequestErrorCode.MIGRATED_USER) {
      this.handleMigratedUser();
    }

    if (error?.code && this.showAuthErrorCodes.includes(error?.code)) {
      if (error?.code === RequestErrorCode.MISSING_RECAPTCHA_MFA) {
        this.alertMessage = "GENERAL.AUTH_CODE_SENT";
      }
    }

    this.checkIfShouldUseRecaptchaV2(error);
    if (this.shouldUseRecaptchaV2) {
      this.handleRecaptchaV2Error(error);
    }

    this.loading = false;
    this.loadingChange.emit(false);

    if (
      error?.code !== RequestErrorCode.MISSING_RECAPTCHA_DATA &&
      error?.code !== RequestErrorCode.LOCATION_SPOOFING_SUSPECTED
    ) {
      this.error = error?.error || error;
      if (this.error && !this.error.message) {
        this.error.message = true;
      }
    }

    if (error?.code === RequestErrorCode.LOCATION_SPOOFING_SUSPECTED) {
      this.handleLocationSpoofingError(error);
    }
  }

  private isSelfExclusionError(error: any): boolean {
    return (
      error?.code === RequestErrorCode.SELF_EXCLUSION_ACTIVE ||
      error?.code === RequestErrorCode.SELF_EXCLUSION_TEMPORARILY
    );
  }

  private handleUserNeedsTwoFactor(data: any) {
    import(
      "~/containers/dialogs/config-two-factor-dialog/config-two-factor-dialog.component"
    ).then((c) => {
      const instance = this.dialog.open(c.ConfigTwoFactorDialogComponent, {
        width: "800px",
      });
      instance.componentInstance.username = data.username;
      this.loading = false;
      this.loadingChange.emit(false);
      this.toggleLoginSideMenuEvent.emit();
    });
  }

  private handleMigratedUser() {
    import(
      "~/containers/dialogs/forgot-password-dialog/forgot-password-dialog.component"
    ).then((c) => {
      const instance = this.dialog.open(c.ForgotPasswordDialogComponent, {
        width: "500px",
      });
      instance.componentInstance.isMigratedUser = true;
    });
  }

  private handleRecaptchaV2Error(error: any) {
    this.alertMessage = "GENERAL.USER_BLOCK_RECAPTCHA";
    if (error?.code === RequestErrorCode.MISSING_RECAPTCHA_MFA) {
      this.alertMessage = "GENERAL.AUTH_CODE_SENT";
    }
    if (this.recaptchaV2) {
      this.recaptchaV2.reset();
    }
    this.showRecaptcha = true;
  }

  private handleLocationSpoofingError(error: any) {
    this.error = error?.error || error;
    const showLocationModal =
      CompanyService.currentCompanyConfig.location?.friendly_modal?.enable;

    if (!this.locationEnabled && showLocationModal) {
      this.checkPermissionStatus();
      const dialog = this.dialog.open(NeedLocationDialogComponent, {
        width: "500px",
      });
      dialog.componentInstance.howToAllowStep = true;
      dialog.componentInstance.dialogTitle = "GENERAL.LOCATION_ERROR.TITLE";
      dialog.componentInstance.dialogImg =
        "../../../../assets/images/need-loc-permission.avif";
      dialog.componentInstance.dialogDescription =
        "GENERAL.LOCATION_ERROR.LOCATION_DISABLED";
      UtilsService.getCurrentLocation(this.authentication);
    } else {
      const dialog = this.dialog.open(StModalConfirmationComponent);
      dialog.componentInstance.title = "GENERAL.LOCATION_ERROR.TITLE";
      dialog.componentInstance.message =
        "GENERAL.LOCATION_ERROR.LOCATION_ENABLED";
      dialog.componentInstance.showCancel = false;
      dialog.componentInstance.labelConfirm = "GENERAL.CLOSE";
      dialog.componentInstance.closeFunc.pipe(takeUntil(this.destroy$)).subscribe(() => {
        dialog.close();
        UtilsService.getCurrentLocation(this.authentication);
      });
    }
  }

  sendMessageLogin() {
    let message = {
      type: "LOGIN",
      tokenExpirationDate: "",
    };
    this.sessionTimeService.sendMessage(message);
  }

  checkPermissionStatus(): void {
    this.permissionLocationStatus = setInterval(() => {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((permission) => {
          permission.onchange = () => {
            if (permission.state === "granted") {
              clearInterval(this.permissionLocationStatus);
              UtilsService.getCurrentLocation(this.authentication);
            }
          };
        });
    }, 1000);
  }

  checkLocation(): void {
    if (!navigator.geolocation) {
      this.locationEnabled = false;
      return;
    }

    if (
      CompanyService.currentCompany.location_restriction?.enable &&
      CompanyService.currentCompany.location_restriction?.actions?.some(
        (action) => action.type === "LOGIN" && action.enable,
      )
    ) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.locationEnabled = true;
          this.authentication.setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          if (error?.code === error.PERMISSION_DENIED) {
            this.locationEnabled = false;
          }
        },
      );
    }
  }

  private verifyKYCRequest(user: User) {
    if (
      user.info?.status === UserStatusEnum.INACTIVE &&
      user.info?.reason ===
        UserStatusReasonEnum.CONFIRMATION_REGISTRATION_DATA &&
      user.type === UserProfile.CLIENT
    ) {
      const dialogInstance = this.dialog.open(
        UserInformationConfirmationDialogComponent,
        { disableClose: true },
      );
      dialogInstance.componentInstance.user = user;
    }
    const uuidCode = UtilsService.generateUUID();

    if (
      user.info?.status === UserStatusEnum.INACTIVE &&
      user.info?.reason === UserStatusReasonEnum.MISSING_KYC
    ) {
      this.kycValidationService.hasRetry = false;
      this.kycValidationService.showKycNotificationModal(
        uuidCode,
        KycTypeEnum.FULL,
        ActionType.REGISTRATION,
        "ACTION",
      );
    }

    if (
      user.info?.status === UserStatusEnum.LOCKED &&
      user.info?.reason === UserStatusReasonEnum.MULTIPLE_LOGIN_ATTEMPTS
    ) {
      this.kycValidationService.hasRetry = false;
      this.kycValidationService.showKycNotificationModal(
        uuidCode,
        KycTypeEnum.FACIAL,
        ActionType.REGISTRATION,
        "LOGIN_LOCKED",
      );
    }
  }

  getMessageError() {
    if (this.error && this.error?.code !== RequestErrorCode.INVALID_2FA_CODE) {
      return this.apiErrorParser.parseError(this.error?.code, this.error.info);
    } else if (
      this.error &&
      (this.error?.code === RequestErrorCode.INVALID_2FA_CODE ||
        this.error?.code === RequestErrorCode.MISSING_RECAPTCHA_MFA)
    ) {
      return this.apiErrorParser.parseError(this.error?.code, this.error.info);
    }
    return "";
  }

  forgotPassword() {
    import("~/containers/dialogs/forgot-password-dialog/forgot-password-dialog.component").then(
      (c) => {
        this.dialog.open(c.ForgotPasswordDialogComponent, { width: "500px" });
      },
    );
  }

  openRegisterScreen(): void {
    if (UtilsService.isDesktop()) {
      this.helper.toggleLoginMenu.emit();
    }
    if (this.company.registration_mode === RegistrationMode.GLOBAL) {
      this.router.navigate(["register-user"]);
    } else {
      this.authentication.openRegisterScreen(this.location, true);
    }
  }

  focusUsername() {
    this.usernameInput?.inputElement?.nativeElement?.focus();
  }

  protected saveWithRecaptchaV3(data: any) {
    this.recaptchaV3Service.execute("importantAction").pipe(takeUntil(this.destroy$)).subscribe((token) => {
      let type = "V3";
      if (!this.showRecaptcha) {
        token = "";
        type = "";
      }
      this.login(data, token, type);
    });
  }

  protected saveWithRecaptchaV2(data: any) {
    if (this.recaptchaV2Token) {
      this.login(data, this.recaptchaV2Token, "V2");
    } else {
      this.recaptchaV2Token = null;
      this.snackbarService.showSuccess(
        this.translate.instant("FEEDBACK_MESSAGE.MISSING_SOLVE_RECAPTCHA"),
      );
      this.loading = false;
    }
  }
}
